import React, { useState, useEffect } from "react";
import Header from "./Header";
import PersonalInformation from "./PersonalInformation";
import CurrentPosition from "./CurrentPosition";
import Skills from "./Skills";
import Website from "./Website";
import OtherInformation from "./OtherInformation";
import axios from "axios";
import { getApiUrl } from "../config/api";
import { Container } from "@/Components/ui/container";
import { Stack } from "@/Components/ui/layout";
import { Heading } from "@/Components/ui/typography";
import { Button } from "@/Components/ui/button";
import { Card, CardContent } from "@/Components/ui/card";
import { Separator } from "@/Components/ui/separator";
import { cn } from "@/lib/utils";
import { toast } from 'sonner';

function LoginProfile() { 
  const [userid, setUserid] = useState(null);
  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    bio: "",
  });
  const [currentPosition, setCurrentPosition] = useState({
    title: "",
    employmentType: "",
    companyName: "",
    currentRole: false,
    location: "",
    locationType: "",
    startMonth: "",
    startYear: "",
    endMonth: "",
    endYear: "",
  });
  const [skills, setSkills] = useState([]);
  const [websites, setWebsites] = useState({ website: "" });
  const [otherInfo, setOtherInfo] = useState({
    jobStatus: "",
    workLocation: "",
    preferredLocation: "",
    companyStages: "",
    techSectors: "",
  });
  const [profileImage, setProfileImage] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      // Debug log removed — was leaking auth token to console.
      if (!token) {
        console.error("No token found. Redirecting to login.");
        window.location.href = "/login";
        return;
      }
      try {
        const response = await axios.get(getApiUrl("/profile"), {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        });

        const { data } = response.data;

        const startDate = data.startDate ? new Date(data.startDate) : null;
        const endDate = data.endDate ? new Date(data.endDate) : null;
        setUserid(data.id);
        
        setPersonalInfo({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          city: data.city,
          bio: data.bio,
        });
        setCurrentPosition({
          title: data.title,
          employmentType: data.employmentType,
          companyName: data.companyName,
          currentRole: data.currentRole,
          location: data.location,
          locationType: data.locationType,
          startMonth: startDate ? startDate.getMonth() + 1 : "",
          startYear: startDate ? startDate.getFullYear() : "",
          endMonth: endDate ? endDate.getMonth() + 1 : "",
          endYear: endDate ? endDate.getFullYear() : "",
        });
        setSkills(data.skills);
        setWebsites({
          website: data.website,
        });
        
        setOtherInfo({
          jobStatus:data.jobStatus,
          workLocation:data.workLocation,
          preferredLocation:data.preferredLocation,
          companyStages:data.companyStages,
          techSectors:data.techSectors,
        });
      } catch (error) {
        console.error("Error fetching profile:", error.response?.data || error.message);
        toast.error("Error fetching profile. Please try again later.");
      }
    };
  
    fetchProfile();
  }, []);

  const handleProfileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setProfileImage(url);
    }
  };

  const handleBannerUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setBannerImage(url);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const payload = {
        userId: userid,
        personalInfo,
        currentPosition,
        skills,
        websites: websites.website,
        otherInfo,
      };
  
      const response = await axios.post(getApiUrl("/profile"), payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          'Content-Type': 'application/json',
        },
      });
      
      toast.success("Profile saved successfully!");
    } catch (error) {
      console.error("Error saving profile:", error.response?.data || error.message);
      toast.error("An error occurred while saving your profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="flex flex-col items-center overflow-hidden py-7 px-20 pb-[103px]">
      <Container size="default" className="w-full max-w-[1202px] flex flex-col items-center">
        <Header />

        <Heading 
          level="h1" 
          className="self-stretch mt-21 text-foreground text-center p-2.5 font-poppins"
        >
          Let's Uncover Your Next Big Adventure!
          <br />
          What kind of journey are you ready to embark on?
        </Heading>

        {/* Banner Section */}
        <div className="relative w-full h-[300px] bg-[#c4c1c1] mt-[8%] border-[3px] border-foreground rounded-[20px]">
          {bannerImage ? (
            <div className="relative">
              <img 
                className="w-full h-[300px] object-cover border-[3px] border-foreground rounded-[20px]" 
                src={bannerImage} 
                alt="Banner" 
              />
              <Button
                variant="destructive"
                className="absolute top-2.5 right-2.5 py-2 px-2 rounded"
                onClick={() => setBannerImage(null)}
              >
                Remove Banner
              </Button>
            </div>
          ) : (
            <label className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#393939] text-white py-2 px-4 rounded cursor-pointer font-poppins">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleBannerUpload}
              />
              Banner +
            </label>
          )}
        </div>

        {/* Profile Section */}
        <div className="flex justify-center self-start -mt-[70px] ml-10 py-2.5">
          <div className="relative text-center">
            {profileImage ? (
              <>
                <img 
                  className="w-[250px] h-[250px] rounded-full border-[3px] border-white object-cover" 
                  src={profileImage} 
                  alt="Profile" 
                />
                <Button
                  variant="outline"
                  className="mt-2.5 bg-[#313030] text-white border-none py-2 px-2 rounded-[20px] -ml-[50px]"
                  onClick={() => setProfileImage(null)}
                >
                  Delete
                </Button>
              </>
            ) : (
              <label className="bg-[#454545] text-white py-2 px-4 rounded cursor-pointer font-poppins">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleProfileUpload}
                />
                Profile Picture +
              </label>
            )}
          </div>
        </div>

        {/* Child Components */}
        <Stack spacing="lg" className="mt-12.5">
          <Card>
            <CardContent className="p-6">
              <PersonalInformation
                personalInfo={personalInfo}
                setPersonalInfo={setPersonalInfo}
              />
            </CardContent>
          </Card>
          
          <Separator className="w-[94%] ml-[30px]" />
          
          <Card>
            <CardContent className="p-6">
              <CurrentPosition
                currentPosition={currentPosition}
                setCurrentPosition={setCurrentPosition}
              />
            </CardContent>
          </Card>
          
          <Separator className="w-[94%] ml-[30px]" />
          
          <Card>
            <CardContent className="p-6">
              <Skills
                skills={skills}
                setSkills={setSkills}
              />
            </CardContent>
          </Card>
          
          <Separator className="w-[94%] ml-[30px]" />
          
          <Card>
            <CardContent className="p-6">
              <Website
                websites={websites}
                setWebsites={setWebsites}
              />
            </CardContent>
          </Card>
          
          <Separator className="w-[94%] ml-[30px]" />
          
          <Card>
            <CardContent className="p-6">
              <OtherInformation
                otherInfo={otherInfo}
                setOtherInfo={setOtherInfo}
              />
            </CardContent>
          </Card>
          
          <Separator className="w-[94%] ml-[30px]" />
          
          {/* Save Button */}
          <Button
            variant="default"
            className="self-end ml-[990px] mb-7.5 text-base py-2.5 px-2.5 bg-[#61ae66] text-foreground hover:bg-[#61ae66]/90"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Profile"}
          </Button>
        </Stack>
      </Container>
    </main>
  );
}

export default LoginProfile;
